import { SendOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Modal, notification, Row, DatePicker } from "antd";
import React, {useEffect, useState} from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import AccountBankOwnerInput, { fioTextLength } from "../Accounts/AccountBankOwnerInput";

const normalizeDiacritics = (str = "") => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

// FIO rejects the whole batch when a message is longer than its import allows: 35 per T2 (EUR) line, 140 for domestic
const EUR_MESSAGE_MAX_LENGTH = 35;
const DOMESTIC_MESSAGE_MAX_LENGTH = 140;

const messageMaxLengthOf = (type) => {
  if (type === "foreign") {
    return EUR_MESSAGE_MAX_LENGTH;
  }

  if (type === "domestic") {
    return DOMESTIC_MESSAGE_MAX_LENGTH;
  }

  return undefined;
};

const getInitialDueDate = () => {
  return moment().date() <= 16 ? moment().date(16) : null;
};

const getLegacyRefundAddress = (client) => {
  return [
    client?.city_branch?.name,
    client?.billing_address_street,
    client?.billing_address_house_number,
  ]
    .filter(Boolean)
    .join(" ");
};

// Account exactly as the backend pays it: the IBAN for foreign (EUR) refunds, the number and bank code for domestic ones
const formatBankAccount = (recipient) => {
  if (!recipient?.account_number_to) {
    return "";
  }

  return recipient.type === "domestic"
    ? `${recipient.account_number_to}/${recipient.account_bank_code}`
    : recipient.account_number_to;
};

const getBuildingIdentifier = (invoice) => {
  return invoice?.building?.reference || invoice?.building?.registration_number || "";
};

const buildDefaultRefundMessage = (invoice, client, t, maxLength) => {
  const period = moment(invoice?.date_issued).add(1, "days").format("YYYY/MM");
  const buildingIdentifier = getBuildingIdentifier(invoice);
  const fallbackAddress = getLegacyRefundAddress(client);
  const suffix = normalizeDiacritics(`-${t("finance.moneyReturn.overpayment")}`);
  let location = normalizeDiacritics(buildingIdentifier || fallbackAddress);

  if (maxLength) {
    // keep the period and suffix, shorten only the building reference
    const budget = maxLength - period.length - suffix.length - 1;
    location = budget > 0 ? location.slice(0, budget).replace(/[\s,./-]+$/, "") : "";
  }

  if (!location) {
    return `${period}${suffix}`;
  }

  return `${period} ${location}${suffix}`;
};

const MoneyReturnModal = ({ invoice, client, total, loading, setLoading }) => {
  const { t } = useTranslation();

  const { TextArea } = Input;

  const [form] = Form.useForm();

  const [isOpen, setIsOpen] = useState(false);
  const [dateDue, setDateDue] = useState(getInitialDueDate());
  // Client the refund goes to (the building's client) with the payment type and account the backend will use,
  // the bank checks the holder name of foreign (EUR) refunds
  const [recipient, setRecipient] = useState(null);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const isEur = recipient?.type === "foreign";
  const messageMaxLength = messageMaxLengthOf(recipient?.type);
  const defaultMessage = buildDefaultRefundMessage(invoice, client, t, messageMaxLength);
  const invoiceId = invoice?.corrective_document?.id || invoice?.id;
  // the invoice's own branch first, the client's first building may be in another country
  const countryBranchIdTo = invoice?.city_branch?.country_branch_id || client?.city_branch?.country_branch_id;
  const needsAccountBankOwner = isEur && !recipient.account_bank_owner_valid;
  const bankAccount = formatBankAccount(recipient);

  const fetchRecipient = () => {
    setRecipient(null);
    setRecipientLoading(true);
    coreApi
      .get(`payments/client-refund-recipient/${invoiceId}`, { params: { country_branch_id_to: countryBranchIdTo } })
      .then((res) => {
        setRecipient(res.data);
        form.setFieldsValue({
          account_bank_owner: res.data.account_bank_owner || undefined,
          // keep a message the user started typing while the recipient was loading
          ...(!form.isFieldTouched("message") && {
            message: buildDefaultRefundMessage(invoice, client, t, messageMaxLengthOf(res.data.type)),
          }),
        });
      })
      .catch((err) => {
        notification.error({ message: err?.response?.data?.message || t("common.error") });
      })
      .finally(() => setRecipientLoading(false));
  };

  useEffect(() => {
    if (isOpen) {
      form.setFieldValue("account_to", bankAccount);
    }
  }, [bankAccount, form, isOpen]);

  const openModal = () => {
    const initialDateDue = getInitialDueDate();

    setDateDue(initialDateDue);
    form.resetFields(["message"]);
    form.setFieldsValue({
      variable_symbol: invoice?.corrective_document?.invoice_number || invoice?.invoice_number,
      amount: Math.abs(total).toFixed(2),
      message: defaultMessage,
      transaction_date: initialDateDue,
    });
    fetchRecipient();
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleOk = async () => {
    if (!bankAccount) {
      notification.error({ message: t("finance.moneyReturn.accountDataNotFilled")});
      return;
    }
    if (!dateDue) {
      notification.error({ message: t("finance.moneyReturn.dateRequired")});
      return;
    }

    const message = form.getFieldValue("message")?.trim() || defaultMessage;

    if (messageMaxLength && fioTextLength(message) > messageMaxLength) {
      notification.error({
        message: t(isEur ? "finance.moneyReturn.messageTooLongEur" : "finance.moneyReturn.messageTooLong", { max: messageMaxLength }),
      });
      return;
    }

    let accountBankOwner;
    if (needsAccountBankOwner) {
      try {
        accountBankOwner = (await form.validateFields(["account_bank_owner"])).account_bank_owner;
      } catch (e) {
        return;
      }
    }

    setLoading(true);
    let payload = {
      country_branch_id_to: countryBranchIdTo,
      account_id_from: invoice?.city_branch?.account_id || client?.city_branch?.account_id,
      client_id_to: client?.id,
      amount: total,
      date: moment(dateDue)?.format("YYYY-MM-DD"),
      variable_symbol: invoice?.corrective_document?.invoice_number || invoice?.invoice_number,
      message: message,
      invoice_id: invoiceId,
      ...(accountBankOwner && { account_bank_owner: accountBankOwner }),
    };

    coreApi
      .post("payments/save-single-client-payment", { transactions: [{ ...payload }] })
      .then((res) => {
        notification.success({ message: res.data.message });
        closeModal();
      })
      .catch((err) => {
        notification.error({
          message: err?.response?.data?.message,
          description: err?.response?.data?.error,
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <React.Fragment>
      <Button onClick={openModal} type="secondary" size="small">
        <SendOutlined />
      </Button>
      <Modal
        key={client?.id}
        open={isOpen}
        onCancel={closeModal}
        title={t("buildings.moneyReturn")}
        okText={t("finance.moneyReturn.sendPaymentForApproval")}
        onOk={handleOk}
        okButtonProps={{ loading: loading || recipientLoading }}
      >
        <Form form={form} layout="inline">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="account_to" label={t("finance.accounts.accountTo")}>
                <Input disabled={true} bordered={false}></Input>
              </Form.Item>
            </Col>

            {needsAccountBankOwner && (
              <Col span={24}>
                <AccountBankOwnerInput
                  required
                  initialValue={recipient.account_bank_owner || undefined}
                  extra={
                    <>
                      {t("finance.moneyReturn.accountBankOwnerMissing")}
                      {recipient.payer_name_hint && (
                        <>
                          <br />
                          {t("finance.moneyReturn.payerNameHint", { name: recipient.payer_name_hint })}
                        </>
                      )}
                    </>
                  }
                />
              </Col>
            )}

            <Col span={24}>
              <Form.Item
                name="variable_symbol"
                label={t("finance.transactions.variableSymbol")}
              >
                <Input disabled={true} bordered={false}></Input>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="amount"
                label={t("finance.moneyReturn.amount")}
              >
                <Input disabled={true} bordered={false}></Input>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="message"
                label={t("finance.transactions.message")}
                // a TextArea counter is drawn under the field, an extra text there would overlap it
                tooltip={isEur && t("finance.moneyReturn.messageTooLongEur", { max: EUR_MESSAGE_MAX_LENGTH })}
              >
                <TextArea rows={2} maxLength={messageMaxLength} showCount={isEur}></TextArea>
              </Form.Item>
            </Col>

            <Col span={16}>
              <Form.Item label={t("finance.moneyReturn.date")} name={"transaction_date"}>
                <DatePicker
                  allowClear={false}
                  onChange={(value) => setDateDue(value)}
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current.format("YYYY-MM-DD") < moment().format("YYYY-MM-DD")
                  }
                ></DatePicker>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default MoneyReturnModal;
